import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand.jsx";
import { useAuth } from "@/lib/auth.jsx";
import { EmptyState, PageHeader, Pill, SectionCard, StatCard } from "@/components/ui-bits";
import { formatINR } from "@/lib/format";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

const BRONZE = "#c5a059";
const LIGHT_GRID = "#ebe7df";
const LIGHT_AXIS = "#7c7569";
const LIGHT_TOOLTIP = {
  background: "#ffffff",
  border: "1px solid #e7e1d8",
  borderRadius: 12,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
};
const COLORS = ["#c5a059", "#e7cf9b", "#8e7a4d", "#d7dbe3", "#8b95a7", "#5c667a"];

const activityTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const partnerCards = (kpis) => [
  { label: "My Leads", value: kpis.my_leads ?? 0, accent: true },
  { label: "New Leads", value: kpis.new_leads ?? 0 },
  { label: "Contacted", value: kpis.contacted ?? 0 },
  { label: "Meeting Scheduled", value: kpis.meeting_scheduled ?? 0 },
  { label: "Follow-up Pending", value: kpis.follow_up_pending ?? 0 },
  { label: "Won", value: kpis.won ?? 0 },
  { label: "Lost", value: kpis.lost ?? 0 },
  { label: "Total Prospects Added", value: kpis.total_prospects_added ?? 0 },
  { label: "Lead Conversion %", value: `${kpis.lead_conversion_pct ?? 0}%` },
];

export default function Dashboard() {
  const { brandQuery, activeBrand, activeBrandId, canSelectAll } = useBrand();
  const { isPartner } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const dashboardQuery = useMemo(
    () => (brandQuery.brand_id ? { brand_id: brandQuery.brand_id } : {}),
    [brandQuery.brand_id]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/dashboard/stats", { params: dashboardQuery })
      .then((r) => {
        if (!active) return;
        setStats(r.data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dashboardQuery]);

  const recentActivity = stats?.recent_activity || [];
  const kpis = stats?.kpis || {};
  const monthly = useMemo(() => stats?.monthly || [], [stats?.monthly]);
  const funnel = useMemo(() => stats?.lead_funnel || [], [stats?.lead_funnel]);
  const brandPerf = useMemo(() => stats?.brand_performance || [], [stats?.brand_performance]);
  const invoiceStatus = useMemo(() => stats?.invoice_status || [], [stats?.invoice_status]);
  const showPartnerMode = isPartner || stats?.mode === "partner";
  const headerEyebrow = activeBrand
    ? activeBrand.name
    : canSelectAll && activeBrandId === "all"
      ? "All Brands"
      : "Assigned Brands";

  const partnerFunnel = useMemo(
    () => (funnel || []).filter((item) => item.count > 0),
    [funnel]
  );

  if (loading) {
    return (
      <div data-testid="dashboard-page">
        <PageHeader
          eyebrow={headerEyebrow}
          title={showPartnerMode ? "Lead Dashboard" : "Operations Cockpit"}
          description={
            showPartnerMode
              ? "Track your own business development pipeline and follow-ups."
              : "A unified view of revenue, receivables, projects, and pipeline health."
          }
        />
        <div className="text-sm text-muted-foreground">Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow={headerEyebrow}
        title={showPartnerMode ? "Lead Dashboard" : "Operations Cockpit"}
        description={
          showPartnerMode
            ? "Only your owned leads, meetings, follow-ups, and recent sales activity are shown here."
            : "A real-time view of revenue, receivables, projects, and brand performance across Acharya Holdings."
        }
      />

      {showPartnerMode ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            {partnerCards(kpis).map((card) => (
              <StatCard
                key={card.label}
                label={card.label}
                value={card.value}
                accent={card.accent}
                testId={`partner-kpi-${card.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.2fr,0.8fr] gap-5">
            <SectionCard title="Recent Activity">
              {recentActivity.length === 0 ? (
                <EmptyState
                  title="No activity yet"
                  description="Timeline updates from your leads will appear here as you work the pipeline."
                />
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((item, index) => (
                    <div
                      key={`${item.lead_id}-${item.created_at}-${index}`}
                      className="rounded-xl border border-border bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-slate-900">{item.lead_name}</div>
                          <div className="mt-1 text-sm text-slate-700">{item.activity}</div>
                          {item.comment ? (
                            <div className="mt-1 text-sm text-muted-foreground">{item.comment}</div>
                          ) : null}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs text-muted-foreground">{activityTime(item.created_at)}</div>
                          <div className="mt-2 text-[11px] text-slate-500">{item.user_name || "System"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <div className="space-y-5">
              <SectionCard title="Pipeline Breakdown">
                {partnerFunnel.length === 0 ? (
                  <EmptyState
                    title="No pipeline yet"
                    description="Lead stage counts will appear here once prospects are added."
                  />
                ) : (
                  <div className="space-y-3">
                    {partnerFunnel.map((item) => (
                      <div key={item.stage} className="rounded-xl border border-border bg-[#fafaf9] p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-medium text-slate-900">{item.stage}</div>
                          <Pill>{item.count}</Pill>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Working Focus">
                <div className="grid grid-cols-1 gap-3">
                  <div className="rounded-xl border border-border bg-[#fafaf9] p-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Meetings scheduled</div>
                    <div className="mt-2 text-2xl font-display font-semibold text-slate-900">
                      {kpis.meeting_scheduled ?? 0}
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-[#fafaf9] p-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Follow-up pending</div>
                    <div className="mt-2 text-2xl font-display font-semibold text-slate-900">
                      {kpis.follow_up_pending ?? 0}
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <StatCard label="Revenue" value={formatINR(kpis.total_revenue)} accent testId="kpi-revenue" />
            <StatCard
              label="Outstanding"
              value={formatINR(kpis.outstanding)}
              sub={`${kpis.pending_count ?? 0} pending`}
              testId="kpi-outstanding"
            />
            <StatCard label="Active Leads" value={kpis.active_leads ?? 0} testId="kpi-leads" />
            <StatCard label="Active Projects" value={kpis.active_projects ?? 0} testId="kpi-projects" />
            <StatCard label="Monthly Expense" value={formatINR(kpis.monthly_expenses)} testId="kpi-expense" />
            <StatCard
              label="GST Collected"
              value={formatINR(kpis.gst_collected)}
              sub={`Input: ${formatINR(kpis.gst_input)}`}
              testId="kpi-gst"
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6">
            <SectionCard title="Revenue vs Expenses" className="xl:col-span-2">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthly}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BRONZE} stopOpacity={0.45} />
                        <stop offset="100%" stopColor={BRONZE} stopOpacity={0.04} />
                      </linearGradient>
                      <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#b8c0cc" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#b8c0cc" stopOpacity={0.08} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={LIGHT_GRID} vertical={false} />
                    <XAxis dataKey="month" stroke={LIGHT_AXIS} fontSize={11} />
                    <YAxis stroke={LIGHT_AXIS} fontSize={11} />
                    <Tooltip contentStyle={LIGHT_TOOLTIP} />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke={BRONZE} fill="url(#rev)" strokeWidth={2.25} />
                    <Area type="monotone" dataKey="expenses" stroke="#98a2b3" fill="url(#exp)" strokeWidth={2.25} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard title="Invoice Status">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={invoiceStatus}
                      dataKey="count"
                      nameKey="status"
                      innerRadius={58}
                      outerRadius={92}
                      paddingAngle={2}
                    >
                      {invoiceStatus.map((_, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} stroke="#fffdf8" />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={LIGHT_TOOLTIP} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-6">
            <SectionCard title="Brand Performance">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={brandPerf} layout="vertical">
                    <CartesianGrid stroke={LIGHT_GRID} horizontal={false} />
                    <XAxis type="number" stroke={LIGHT_AXIS} fontSize={11} />
                    <YAxis dataKey="code" type="category" stroke={LIGHT_AXIS} fontSize={11} width={42} />
                    <Tooltip contentStyle={LIGHT_TOOLTIP} />
                    <Legend />
                    <Bar dataKey="paid" stackId="a" fill={BRONZE} radius={[0, 6, 6, 0]} />
                    <Bar dataKey="outstanding" stackId="a" fill="#d7dbe3" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard title="Lead Funnel">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnel}>
                    <CartesianGrid stroke={LIGHT_GRID} vertical={false} />
                    <XAxis
                      dataKey="stage"
                      stroke={LIGHT_AXIS}
                      fontSize={10}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={74}
                    />
                    <YAxis stroke={LIGHT_AXIS} fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={LIGHT_TOOLTIP} />
                    <Bar dataKey="count" fill={BRONZE} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </div>

          <SectionCard title="Recent Activity">
            {recentActivity.length === 0 ? (
              <EmptyState
                title="No activity logged yet"
                description="Lead updates, stage movements, and timeline events will appear here."
              />
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {recentActivity.map((item, index) => (
                  <div
                    key={`${item.lead_id}-${item.created_at}-${index}`}
                    className="rounded-xl border border-border bg-[#fafaf9] p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-slate-900">{item.lead_name}</div>
                        <div className="mt-1 text-sm text-slate-700">{item.activity}</div>
                        {item.comment ? (
                          <div className="mt-1 text-sm text-muted-foreground">{item.comment}</div>
                        ) : null}
                      </div>
                      <div className="text-right shrink-0 text-xs text-muted-foreground">
                        <div>{activityTime(item.created_at)}</div>
                        <div className="mt-1">{item.user_name || "System"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
